import React, { Component } from 'react';

import APIDocsCurl from 'common/developers/docs/api/APIDocsCurl';
import APIDocsMethod from 'common/developers/docs/api/APIDocsMethod';
import Highlight from 'common/developers/docs/api/Highlight';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

export default class APIDocsCreateOrUpdateUserBlock extends Component {
  createdAt = new Date();

  render() {
    return (
      <APIDocsBlock>
        <div>
          <div className="heading">Create or update user</div>
          <div className="text">
            If the user does not exist, one is created, and its id is returned. If the user does
            exist, they will be updated with the data supplied.
          </div>
          <div className="text">
            Why is this endpoint useful? If you're going to use an object creation API endpoint
            (such as create post), you may have to create the author's account before creating the
            post.
          </div>
          <div className="text">
            <div className="emphasize">Note: </div> the email, name and avatarURL properties of
            admin users cannot be modified through the Canny API. A Canny admin will need to login
            to Canny to update this information manually.
          </div>
          <div className="text">
            <div className="emphasize">Note: </div> one of email, userID or id must be provided to
            update a user.
          </div>
          <APIDocsTable
            title="Arguments"
            rows={[
              {
                description: 'Your secret API key.',
                heading: 'apiKey',
                subheading: 'string',
              },
              {
                description: "The user's alias",
                heading: 'alias',
                subheading: 'string (optional)',
              },
              {
                description: "The URL pointing to the user's avatar image.",
                heading: 'avatarURL',
                subheading: 'string (optional)',
              },
              {
                description: (
                  <p>
                    Defines the list of companies the user is associated with. To associate a user
                    with a company, the user must have a UserID. Verify the UserID using the{' '}
                    <a href="#retrieve_user">retrieve user</a> endpoint.
                    <br />
                    <br />
                    Omitting a company that the user is currently associated with will not
                    disassociate the user from that company. To disassociate a user from a company,
                    use the <a href="#remove_user_from_company">remove user from a company</a>{' '}
                    endpoint.
                  </p>
                ),
                heading: 'companies',
                subheading: 'array (optional)',
              },
              {
                description: 'The date the user was created in your system.',
                heading: 'created',
                subheading: 'date (optional)',
              },
              {
                description:
                  'Any custom fields associated with the user. Each field name (key) must be between 0 and 30 characters long. If field values are strings, they must be less than 200 characters long.',
                heading: 'customFields',
                subheading: 'object (optional)',
              },
              {
                description: "The user's email.",
                heading: 'email',
                subheading: 'string (optional)',
              },
              {
                description: (
                  <p>
                    The user's unique identifier in Canny. This can be found through the{' '}
                    <a href="#list_users">list users</a> endpoint.
                  </p>
                ),
                heading: 'id',
                subheading: 'string (optional)',
              },
              {
                description: "The user's name. Must be between 1 and 50 characters.",
                heading: 'name',
                subheading: 'string',
              },
              {
                description: "The user's unique identifier in your application.",
                heading: 'userID',
                subheading: 'string (optional)',
              },
            ]}
          />
          <div className="subheading">Returns</div>
          <div className="text">Upon success, returns an object with a single key: id.</div>
          <div className="text">
            You can then use this id in object creation API endpoints such as for creating posts.
          </div>
        </div>
        <div>
          <div className="text">Endpoint</div>
          <Highlight lang="http">https://canny.io/api/v1/users/create_or_update</Highlight>
          <Highlight lang="http">
            https://canny.io/api/v1/users/find_or_create (deprecated)
          </Highlight>
          <APIDocsMethod />
          <APIDocsCurl
            path="https://canny.io/api/v1/users/find_or_create"
            body={`-d '{
    "apiKey": "YOUR_API_KEY",
    "alias": "Green Fish",
    "companies": [{
      "created": "${this.createdAt.toISOString()}",
      "customFields": {
        "field1": "value1",
        "field2": "value2"
      },
      "id": "company123",
      "monthlySpend": 500.00,
      "name": "company name"
    }],
    "created": "${this.createdAt.toISOString()}",
    "customFields": {
      "field1": "value1",
      "field2": "value2"
    },
    "email": "sally@netflix.com",
    "name": "Sally Doe",
    "userID": "1234-5678"}' \\
    -H "Content-Type: application/json"`}
          />
          <div className="text">Example Response</div>
          <Highlight lang="json">
            {`{
  "id": "553c3ef8b8cdcd1501ba123a",
}`}
          </Highlight>
        </div>
      </APIDocsBlock>
    );
  }
}
